import * as XLSX from "xlsx";
import { iEnquiry } from "../../interfaces/iEnquiry";
import Statuses from "../../interfaces/Statuses";

export const MAX_EXPORT = 1000;
export const EXPORT_DATE_FORMAT = "dd/mm/yyyy hh:mm";
export const EXPORT_HEADERS = [
    "Priority",
    "Mailbox",
    "Reference",
    "Owner",
    "Enquirer Name",
    "Received",
    "Timeout",
    "Status",
    "Subject",
    "Completed",
    "Items",
    "Response Timeout",
];
const DATE_COLUMNS = [5, 6, 9, 11];
const DATE_COLUMN_WIDTH = 16;
const MAX_COLUMN_WIDTH = 60;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86400000;

export interface ExportMailbox {
    id: number;
    name: string;
}

export type ExportCell = string | number | undefined;

// Uses the wall-clock part of the string itself, the same way GridDateTimeDisplay does,
// so Excel shows the time the grid shows whatever the browser's timezone.
export function toExcelSerial(iso?: string | null): number | undefined {
    const match = iso ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(iso) : null;
    if (!match) return undefined;
    const [year, month, day, hour, minute, second] = match.slice(1).map(x => parseInt(x ?? "0", 10));
    if (year < 1900) return undefined;
    return (Date.UTC(year, month - 1, day, hour, minute, second) - EXCEL_EPOCH) / MS_PER_DAY;
}

function pad(value: number): string {
    return value.toString().padStart(2, "0");
}

export function exportFileName(now: Date): string {
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    return `${date} ${time}.xlsx`;
}

export function buildEnquiryExportRows(enquiries: iEnquiry[], mailboxes: ExportMailbox[]): ExportCell[][] {
    const mailboxNames = new Map(mailboxes.map(x => [x.id, x.name]));
    return enquiries.map(e => [
        e.priority,
        mailboxNames.get(e.mailbox) ?? String(e.mailbox),
        e.enquiryReference,
        (e.owners ?? []).map(o => `${o.firstname} ${o.lastname}`).join(", "),
        e.enquirerName,
        toExcelSerial(e.dateReceived),
        toExcelSerial(e.activeSlaCycle?.reminder),
        Statuses.Name(e.status) ?? "",
        e.subject,
        toExcelSerial(e.dateCompleted),
        e.workItems,
        toExcelSerial(e.activeSlaCycle?.timeout),
    ]);
}

export function buildEnquiryWorkbook(rows: ExportCell[][]): XLSX.WorkBook {
    const ws = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...rows]);
    for (let r = 1; r <= rows.length; r++) {
        for (const c of DATE_COLUMNS) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell) cell.z = EXPORT_DATE_FORMAT;
        }
    }
    ws["!cols"] = EXPORT_HEADERS.map((header, c) => ({
        wch: DATE_COLUMNS.includes(c)
            ? DATE_COLUMN_WIDTH
            : Math.min(MAX_COLUMN_WIDTH, Math.max(header.length, ...rows.map(row => String(row[c] ?? "").length))) +
              2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    return wb;
}

export function exportEnquiriesToExcel(
    enquiries: iEnquiry[],
    mailboxes: ExportMailbox[],
    now: Date = new Date(),
): { count: number; fileName: string } {
    const toExport = enquiries.slice(0, MAX_EXPORT);
    const wb = buildEnquiryWorkbook(buildEnquiryExportRows(toExport, mailboxes));
    const fileName = exportFileName(now);
    XLSX.writeFile(wb, fileName);
    return { count: toExport.length, fileName };
}
